import { TweenLite } from "gsap";
import { Color, CreateElement, Geometry, HorizontalAlign, LogGroup, MakeColorGradient, Point, Rect, SetStyles, VerticalAlign } from "../../.././MyHelpers";
import { ResourceIdentifier } from "../../../ResourceManager/ResourceIdentifier";
import { IReadOnlyResourceManager, IWriteOnlyResourceManager } from "../../../ResourceManager/ResourceManager";
import { SpriteResource } from "../../../ResourceManager/SpriteResource";
import { JackpotDisplayType, Settings } from "../../../Settings";
import { ZIndex } from "../../../ZIndex";
import { AwardOptionPosition } from "../../../api";
import { IRewardOption } from "../../ScatterRandomAward/ScatterRandomAward";
import { S2_SpinnableReelsBigCanvas2 } from "../../SpinAnimation2/SpinnableReelsBigCanvas2";
import { CanvasSprite } from "../../Sprite/CanvasSprite";
import { Sprite } from "../../Sprite/Sprite";


export interface HoldingFeatureParams
{
    readonly resid: ResourceIdentifier;
    readonly logGroup: LogGroup;
    readonly spinnableReels: S2_SpinnableReelsBigCanvas2;    
    readonly gameResourcesBasePath:string;
    readonly reelsCoverAnotherScreen : boolean ;
}



export class HoldingFeatureItem
{
    protected sprite: Sprite;
    protected canvasSprite: CanvasSprite;
    protected canvasRect: Rect;
    protected rewardOption: IRewardOption;

    protected started: boolean;
    protected symbolPosition: Point;

    protected readonly scale: number = 1.08;

    public constructor(sprite: Sprite, symbolPosition: Point, pixelPosition: Rect, color: Color)
    {
        this.canvasRect = Geometry.ScaleRect(Geometry.CoverRect(sprite.GenericRectangle, pixelPosition, HorizontalAlign.Center, VerticalAlign.Center), this.scale, this.scale);
        this.canvasSprite = new CanvasSprite({sprite: sprite, canvasSize: this.canvasRect.Size()});

        this.canvasSprite.AddOnDrawFrameCallback(() => {
            this.rewardOption?.DrawTo(this.canvasSprite.canvas);
        });

        this.symbolPosition = symbolPosition;
        this.View().classList.add(`Item-x${symbolPosition.x}-y${symbolPosition.y}`);

        SetStyles(this.View(), {
            //border: "5px solid red",

            position: "absolute",
            left: this.canvasRect.x + "px",
            top: this.canvasRect.y + "px",
            zIndex: ZIndex.HoldingFeature.toString(),

            //backgroundColor: color.toRgba(),
            opacity: "0.2"
        });

        //this.canvasSprite.Start();

        TweenLite.set(this.View(), {autoAlpha: 0});
    }

    public SetRewardOption(rewardOption: IRewardOption): void
    {
        this.rewardOption = rewardOption;
    }

    public View(): HTMLElement
    {
        return this.canvasSprite.View();
    }

    public Update(): void
    {
        this.canvasSprite.Update();
    }

    public Start(rewardOption: IRewardOption): void
    {
        //if (this.started) throw `HoldingFeatureItem x${this.symbolPosition.x} y${this.symbolPosition.y} already started`;
        this.SetRewardOption(rewardOption);
        this.started = true;

        TweenLite.fromTo(this.View(), 0.5, {scale: 1/this.scale}, {scale: 1, autoAlpha: 1});
        this.canvasSprite.Start(false);
    }

    public Started(): boolean
    {
        return this.started;
    }

    public Stop(): void
    {
        this.started = false;
        this.canvasSprite.Stop();
        TweenLite.set(this.View(), {autoAlpha: 0});
    }
}


export type HoldingFeatureStartedCallback = () => void;

export class HoldingFeature
{
    protected params: HoldingFeatureParams;
    protected resid: ResourceIdentifier;
    protected logGroup: LogGroup;

    protected view: HTMLElement;

    //protected spinnableReels: SpinnableReels; 

    protected holdingItems: HoldingFeatureItem[][] = [];
    protected flatHoldingItems: HoldingFeatureItem[] = [];

    protected sprite: Sprite;
    // TODO convert to map?
    protected awardOptions: IRewardOption[] = [];

    public constructor(params: HoldingFeatureParams)
    {
        this.params = params;

        this.resid = params.resid.Add("HoldingFeature");
        this.logGroup = params.logGroup.Add("HoldingFeature");
        //this.spinnableReels = params.spinnableReels;

        this.view = CreateElement("div", {classes: ["HoldingFeature"], style: {
            position: "absolute",
            // left: this.params.spinnableReels.GetWindowRect().x + "px",
            // top: this.params.spinnableReels.GetWindowRect().y + "px",
            // width: this.params.spinnableReels.GetWindowRect().width + "px",
            // height: this.params.spinnableReels.GetWindowRect().height + "px",

            left: "0px",
            top: "0px",
            // width: "1920px",
            // height: "1080px",
            pointerEvents: "none",
            zIndex: ZIndex.BonusReelOverlay.toString(),
        }});
    }

    protected readonly symbolsByJackpot = {
        [JackpotDisplayType.RapidDiamondPot]: ["SRA"],
        [JackpotDisplayType.PlayCityLink]: ["SRA"],
        [JackpotDisplayType.RapidDiamondPotStep]: ["SRA"],
    }

    public PrepareResources(resourceManager: IWriteOnlyResourceManager): void {
        let symbolPath = this.params.gameResourcesBasePath + "symbol";

        //use for change for now the symbol Anim for Holding Feature
        if (this.symbolsByJackpot[Settings.JackpotDisplayType]?.includes("SRA")) {
            symbolPath = this.params.gameResourcesBasePath + "SymbolJackpots/" + Settings.JackpotDisplayType + "/symbols2/symbol";
        }
        // TODO share sprite, if its loaded from common?
        resourceManager.Add(new SpriteResource(symbolPath, this.resid.Add("itemsprite"), true, this.logGroup.Add("itemsprite"), true));
        symbolPath = null;
    }

    public async ResourcesLodaded(resourceManager: IReadOnlyResourceManager, awardOptions: IRewardOption[]): Promise<void>
    {
        this.awardOptions = awardOptions;
        this.sprite = resourceManager.Get<SpriteResource>(this.resid.Add("itemsprite")).sprite;

        //let windowSymbolSize: Size = this.params.spinnableReels.GetWindowSymbolSize();

        // for debugging
        let colors: Color[] =  MakeColorGradient(2.4, 2.4, 2.4, 0, 2, 4, 128, 127, 50, 0.5);



        // for (let col = 0; col < windowSymbolSize.width; col++)
        // {
        //     this.holdingItems.push([]);

        //     for (let row = 0; row < windowSymbolSize.height; row++)
        //     {
        //         let reverseRow = windowSymbolSize.height - row -1;
        //         let symbolRect = this.params.spinnableReels.GetSymbolRect(col, reverseRow);
        //         let holdingItem: HoldingFeatureItem = new HoldingFeatureItem(this.sprite.Copy(), new Point(col, reverseRow), symbolRect, colors[this.flatHoldingItems.length]);

        //         holdingItem.SetRewardOption(awardOptions.random());

        //         this.holdingItems[col].push(holdingItem);
        //         this.flatHoldingItems.push(holdingItem);

        //         this.view.appendChild(holdingItem.View());
        //     }
        // }

        // DONT USE COLUMN AND ROW, because there are less symbols than rows x columns, some symbols span over multiple rows/columns

        let reelConfig = this.params.spinnableReels.GetReelConfig();
        for (let reelIndex = 0; reelIndex < reelConfig.length; reelIndex++)
        {
            for (let symbolIndex = 0; symbolIndex < reelConfig[reelIndex].Height; symbolIndex++)
            {
                let symbolRect = this.params.spinnableReels.GetAbsoluteSymbolRectReelRow(reelIndex, symbolIndex);

                let holdingItem: HoldingFeatureItem = new HoldingFeatureItem(
                    this.sprite.Copy(), 
                    new Point(reelIndex, symbolIndex),
                    symbolRect,
                    colors[this.holdingItems.length]
                );

                holdingItem.SetRewardOption(awardOptions.random());

                if (!this.holdingItems) this.holdingItems = [];
                if (!this.holdingItems[reelIndex]) this.holdingItems[reelIndex] = [];


                this.holdingItems[reelIndex].push(holdingItem);
                this.flatHoldingItems.push(holdingItem);

                this.view.appendChild(holdingItem.View());
                symbolRect = null;
                holdingItem = null;
            }
        }
    }

    public SetAwardOptions(awardOptions: IRewardOption[]): void
    {
        this.awardOptions = awardOptions;
    }

    protected startCallbacks: HoldingFeatureStartedCallback[] = [];
    public AddStartCallback(callback: HoldingFeatureStartedCallback): void
    {
        this.startCallbacks.push(callback);
    }

    protected OnStarted(): void
    {
        for (let i = 0; i < this.startCallbacks.length; i++)
        {
            this.startCallbacks[i]();
        }
    }

    public Start(featureResult: AwardOptionPosition[])
    {
        if (featureResult){
            //let windowSymbolSize: Size = this.params.spinnableReels.GetWindowSymbolSize();

            featureResult.forEach(sp => {
                
                let holdingItem = this.holdingItems[sp.Reel][sp.ReelOffset];
                //if (holdingItem.Started()) throw "Already started?"

                let awardOption = this.awardOptions.find(ro => ro.Name() == sp.AwardOption.AwardName);
                if (!awardOption) throw `Award ${sp.AwardOption.AwardName} not loaded?`;

                holdingItem.Start(awardOption);
                awardOption = null;
            });
            featureResult = null;
        }

        this.Show();
    }

    public Stop()
    {
        //let windowSymbolSize: Size = this.params.spinnableReels.GetWindowSymbolSize();

        for (let reel = 0; reel < this.holdingItems.length; reel++)
        {
            for (let sym = 0; sym < this.holdingItems[reel].length; sym++)
            {
                this.holdingItems[reel][sym].Stop();
            }
        }
    }

    public View(): HTMLElement
    {
        return this.view;
    }

    public Update(): void
    {
        // TODO only update visible items?
        for (let i = 0; i < this.flatHoldingItems.length; i++)
        {
            this.flatHoldingItems[i].Update();
        }
    }

    public Show(): void
    {
        SetStyles(this.view, {
            display: ""
        });
    }

    public Hide(): void
    {
        SetStyles(this.view, {
            display: "none"
        });
    }
}
